import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Foot from '../../../components/Common/Footer/Footer';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import {
  IndianRupee,
  Calendar,
  User,
  CreditCard,
  Printer,
  Pencil,
  Wallet,
  Activity,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Briefcase
} from 'lucide-react';
import styles from './ViewInvoice.module.css';
import NotFound from '../../Error/NotFound';


const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/sales-invoices/${id}`);
      if (response.data.success) {
        setInvoice(response.data.data);
      } else {
        toast.error('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Backend connection lost');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || 'pending';
    if (s === 'paid') return { label: 'PAID', style: styles.paid, dot: <CheckCircle2 size={12} /> };
    if (s === 'partial_paid') return { label: 'PARTIAL', style: styles.partial_paid, dot: <Activity size={12} /> };
    if (s === 'overdue') return { label: 'OVERDUE', style: styles.overdue, dot: <AlertCircle size={12} /> };
    return { label: 'PENDING', style: styles.pending, dot: <Clock size={12} /> };
  };

  if (loading) {
    return (
      <div className={styles['view-invoice-container']}>
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={48} className={styles.spinner} />
            <p>Gathering financial data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return <NotFound />;
  }


  const statusConfig = getStatusConfig(invoice.status);
  
  // Calculations
  const totalAmount = parseFloat(invoice.total_amount) || 0;
  const payments = invoice.payments_received || [];
  const amountReceived = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const balance = totalAmount - amountReceived;
  


  return (
    <div className={styles['view-invoice-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'Sales Invoices', path: '/invoices' },
            { label: invoice.invoice_number }
          ]} />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Invoice Details</h1>
              <p>Review billing items, ledger, and project association</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`/invoices/edit/${invoice.id}`)}>
                <Pencil size={18} />
                <span>Edit Invoice</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['invoice-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{invoice.invoice_number}</h1>
                <span className={`${styles['status-badge']} ${statusConfig.style}`}>
                  {statusConfig.dot}
                  {statusConfig.label}
                </span>
              </div>
            </div>

            <div className={styles['invoice-meta']}>
              <div className={styles['meta-item']}>
                <User size={16} />
                <span>Client: <strong>{invoice.project?.customer?.customer_name || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Briefcase size={16} />
                <span>Project: <strong>{invoice.project?.project_name || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Date: <strong>{new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
                <span className={styles['m-trend']}>Total Billed</span>
              </div>
              <div className={styles['m-info']}>
                <label>Invoice Amount</label>
                <h2>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-success']}`}><CreditCard size={20} /></div>
                <span className={styles['m-trend']}>Revenue</span>
              </div>
              <div className={styles['m-info']}>
                <label>Amount Received</label>
                <h2>₹{amountReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${balance > 0 ? styles['bg-warning'] : styles['bg-total']}`}><Wallet size={20} /></div>
                <span className={styles['m-trend']}>Outstanding</span>
              </div>
              <div className={styles['m-info']}>
                <label>Pending Balance</label>
                <h2 style={{ color: balance > 0 ? '#f97316' : '#1e293b' }}>
                  ₹{Math.max(0, balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h2>
              </div>
            </div>


          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            {/* Left Column (70%) */}
            <div className={styles['left-column']}>
              {/* Payments Ledger Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Activity size={18} />
                    <h3>Payment Ledger</h3>
                  </div>
                </div>
                {payments.length > 0 ? (
                  <>
                    <div className={styles['table-scroller']}>
                      <table className={styles['data-table']}>
                        <thead>
                          <tr>
                            <th>RECEIPT ID</th>
                            <th>PAYMENT DATE</th>
                            <th>MODE</th>
                            <th>REFERENCE</th>
                            <th className={styles['text-right']}>AMOUNT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment, idx) => (
                            <tr key={payment.id || idx}>
                              <td className={styles.bold}>{payment.voucher_no || `REC-${payment.id}`}</td>
                              <td>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</td>
                              <td>{['Cash', 'Bank Transfer', 'Cheque', 'Online'][payment.payment_mode - 1] || 'Bank Transfer'}</td>
                              <td><span className={styles['i-ref']}>{payment.ref_transaction_number || '---'}</span></td>
                              <td className={`${styles['text-right']} ${styles.highlight}`}>
                                ₹{parseFloat(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Payment Cards */}
                    <div className={styles['ledger-cards-mobile']}>
                      {payments.map((payment, idx) => (
                        <div key={payment.id || idx} className={styles['payment-card-mobile']}>
                          <div className={styles['p-card-header']}>
                            <div className={styles['p-id']}>{payment.voucher_no || `REC-${payment.id}`}</div>
                            <div className={styles['p-amount']}>₹{parseFloat(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                          </div>
                          <div className={styles['p-card-body']}>
                            <div className={styles['p-detail']}>
                              <label>Date</label>
                              <span>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</span>
                            </div>
                            <div className={styles['p-detail']}>
                              <label>Mode</label>
                              <span>{['Cash', 'Bank Transfer', 'Cheque', 'Online'][payment.payment_mode - 1] || 'Bank Transfer'}</span>
                            </div>
                            <div className={styles['p-detail']}>
                              <label>Reference</label>
                              <span className={styles['i-ref']}>{payment.ref_transaction_number || '---'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={styles['empty-state']}>
                    No payments have been recorded against this invoice yet.
                  </div>
                )}
              </div>
              
              {/* Additional Context */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <FileText size={18} />
                    <h3>Billing Context & Notes</h3>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>
                  {invoice.notes || 'No specific billing instructions or contextual notes provided for this invoice phase.'}
                </p>
              </div>
            </div>

            {/* Right Column (30%) */}
            <div className={styles['right-column']}>
              
              {/* Project & Client Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Briefcase size={18} />
                    <h3>Client & Project</h3>
                  </div>
                </div>
                
                <div className={styles['info-row']}>
                  <div className={styles['info-icon']}><User size={16} /></div>
                  <div className={styles['info-content']}>
                    <label>Client Organization</label>
                    <h4>{invoice.project?.customer?.customer_name || '---'}</h4>
                  </div>
                </div>
                
                <div className={styles['info-row']}>
                  <div className={styles['info-icon']}><Briefcase size={16} /></div>
                  <div className={styles['info-content']}>
                    <label>Linked Project</label>
                    <h4>{invoice.project?.project_name || '---'}</h4>
                  </div>
                </div>
              </div>

              {/* Stakeholders Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <User size={18} />
                    <h3>Processing Staff</h3>
                  </div>
                </div>
                
                <div className={styles['info-row']}>
                  <div className={styles['info-icon']} style={{ background: '#fef3c7', color: '#92400e' }}>
                    {invoice.creator?.name?.[0]?.toUpperCase() || 'A'}
                  </div>
                  <div className={styles['info-content']}>
                    <label>Digitized By</label>
                    <h4>{invoice.creator?.name || 'Administrator'}</h4>
                  </div>
                </div>
              </div>

              {/* Timeline Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Calendar size={18} />
                    <h3>Billing Lifecycle</h3>
                  </div>
                </div>
                
                <div className={styles['timeline-v']}>
                  <div className={styles['tl-item']}>
                    <div className={styles['tl-dot']} style={{ borderColor: '#64748b' }}></div>
                    <div className={styles['tl-content']}>
                      <h4>Invoice Generated</h4>
                      <p>{new Date(invoice.invoice_date).toLocaleDateString('en-IN')}</p>
                    </div>
                  </div>

                  {invoice.status === 'paid' && (
                    <div className={styles['tl-item']}>
                      <div className={styles['tl-dot']} style={{ borderColor: '#10b981' }}></div>
                      <div className={styles['tl-content']}>
                        <h4>Settled</h4>
                        <p>Balance Cleared</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <Foot />
        </main>
      </div>
    </div>
  );
};

export default ViewInvoice;
